<template>
  <div class="card-received-page">
    <!-- 页面标题与开新卡按钮 -->
    <div class="page-header">
      <h2 class="page-title">我收到的 ({{ cardList.length }})</h2>
    </div>

    <!-- 卡片列表 -->
    <div class="card-table">
      <div class="table-header">
        <div class="col-owner">办卡人</div>
        <div class="col-count">使用卡数</div>
        <div class="col-detail">卡详情</div>
        <div class="col-status">状态</div>
        <div class="col-bind-time">绑定时间</div>
        <div class="col-unbind-time">解绑时间</div>
      </div>

      <div v-if="loading" class="loading-tip">
        <el-skeleton :rows="5" animated />
      </div>

      <div v-else-if="cardList.length === 0" class="empty-tip">
        <el-empty description="暂无收到的卡" />
      </div>

      <div 
        class="table-row" 
        v-for="item in cardList" 
        :key="item.id"
      >
        <div class="col-owner">{{ item.mainUserName || '-' }}</div>
        <div class="col-count">{{ item.cardCount || '0' }}张</div>
        <div class="col-detail">
          <div class="card-item" v-for="card in item.itemList" :key="card.id">
            <div class="card-name">{{ card.cardName || '未知卡种' }}</div>
            <div class="card-price" v-if="card.type!=3">¥{{ card.cardCash || '0.00' }}</div>
            <div class="card-price" v-else>{{ card.mainServeCount || '0.00' }}次</div>
            <div class="card-gift">赠送{{ card.cardCashProm > 0 ? `¥${card.cardCashProm}` : `${card.giveServeCount}项服务` }}</div>
            <div class="card-type">{{ getCardTypeText(card.type) }}</div>
            <div class="card-expire">有效期至{{ card.expireTime }}</div>
          </div>
        </div>
        <div class="col-status">
          <el-tag :type="getStatusTagType(item.status)">
            {{ getStatusText(item.status) }}
          </el-tag>
        </div>
        <div class="col-bind-time">{{ item.bindTime }}</div>
        <div class="col-unbind-time">{{ item.unBindTime }}</div>
      </div>
    </div>
  </div>
</template>

<script lang="js">
import { defineComponent, ref, onMounted } from 'vue'
import { ElMessage, ElEmpty, ElTag, ElSkeleton } from 'element-plus'
import moment from 'moment'
import { getFamilyGroupAvailableCard } from '@/service/main/customer'
import { useRoute } from 'vue-router'
export default defineComponent({
  components: {
    ElEmpty,
    ElTag,
    ElSkeleton
  },
  setup() {
    const route = useRoute()
    // 卡片列表数据
    const cardList = ref([])
    // 加载状态
    const loading = ref(false)

    // 状态码映射（接口返回的status -> 显示文本）
    const statusMap = {
      1: '待处理',
      2: '已绑定',
      3: '已解除',
      4: '拒绝'
    }

    // 状态标签类型映射
    const statusTagTypeMap = {
      1: 'warning',  // 待处理-警告色
      2: 'success',  // 已绑定-成功色
      3: 'info',     // 已解除-信息色
      4: 'danger'    // 拒绝-危险色
    }

    // 卡类型映射（根据实际业务补充）
    const cardTypeMap = {
      3: '有限次卡',
      4: '不限次卡',
      5: '储蓄卡',
      6: '折扣卡',
    }

    // 获取状态显示文本
    const getStatusText = (status) => {
      return statusMap[status] || '-'
    }

    // 获取状态标签类型
    const getStatusTagType = (status) => {
      return statusTagTypeMap[status] || 'info'
    }

    // 获取卡类型文本
    const getCardTypeText = (type) => {
      return cardTypeMap[type] || '未知类型'
    }

    // 获取收到的卡列表数据
    const fetchCardList = async () => {
      try {
        loading.value = true
        // 调用接口（替换为实际的接口参数）
        const res = await getFamilyGroupAvailableCard({userId:route.query.userId})
        
        // 处理接口返回数据
        if (res && res.data) {
          // 如果接口返回的是数组，直接赋值；如果是单个对象，转为数组
          cardList.value = Array.isArray(res.data) ? res.data : [res.data]
        } else {
          cardList.value = []
        }
      } catch (error) {
        console.error('获取收到的卡列表失败:', error)
        // ElMessage.error('获取数据失败，请稍后重试')
        cardList.value = []
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      // 页面初始化加载数据
      fetchCardList()
    })

    return {
      cardList,
      loading,
      getStatusText,
      getStatusTagType,
      getCardTypeText
    }
  }
})
</script>

<style scoped lang="less">
.card-received-page {
  padding: 20px;

  // 页面头部
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    .page-title {
      font-size: 16px;
      font-weight: 600;
      color: #303133;
      margin: 0;
    }
  }

  // 表格容器
  .card-table {
    background-color: #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    overflow: hidden;
    border-radius: 8px;
  }

  // 表格头部
  .table-header {
    display: flex;
    background-color: #efefef;
    line-height: 50px;
    padding: 0 20px;
    font-weight: 500;
    color: #606266;
    font-size: 14px;

    > div {
      flex: 1;
      text-align: center;
    }

    .col-owner, .col-detail {
      flex: 1.5;
      text-align: left !important;
    }
  }

  // 加载状态
  .loading-tip {
    padding: 20px;
  }

  // 表格行
  .table-row {
    display: flex;
    align-items: center;
    line-height: 48px;
    padding: 0 20px;
    border-bottom: 1px solid #f0f2f5;
    cursor: pointer;
    transition: background-color 0.2s;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: #f8f9fa;
    }

    > div {
      flex: 1;
      text-align: center;
      color: #606266;
      font-size: 13px;
      word-break: break-all;
    }

    .col-owner, .col-detail {
      flex: 1.5;
      text-align: left !important;
    }

    .col-owner {
      font-weight: 500;
      color: #303133;
    }
  }

  // 卡详情列表
  .card-item {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 4px;
    padding: 4px 0;
    font-size: 12px;
    align-items: center;

    .card-name {
      font-weight: 500;
      color: #303133;
      min-width: 60px;
    }

    .card-price {
      color: #409eff;
    }

    .card-gift {
      color: #67c23a;
    }

    .card-type {
      color: #909399;
      min-width: 50px;
    }

    .card-expire {
      color: #f56c6c;
      font-size: 11px;
    }
  }

  // 空状态提示
  .empty-tip {
    padding: 60px 0;
    text-align: center;
  }
}
</style>